import React, { useMemo, useState } from "react";
import { ChevronRight, LucideIcon, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/**
 * A single node in the navigation tree. Branch nodes have `children`,
 * leaf nodes don't. Kept intentionally generic / app-agnostic — this is a
 * reusable Core primitive, so it carries no Origo domain knowledge.
 */
export interface NavTreeNode {
	/** Stable, unique identifier used for selection and expansion state. */
	id: string;
	/** Display label. Translated via i18next unless `skipTranslation` is set. */
	label: string;
	/** Optional leading icon (lucide-react). */
	icon?: LucideIcon;
	/** Child nodes — presence of a non-empty array makes this a branch. */
	children?: NavTreeNode[];
	/** Render the node greyed-out and non-interactive. */
	disabled?: boolean;
}

export interface AppNavTreeProps {
	/** The tree to render. */
	nodes: NavTreeNode[];
	/** Id of the currently selected node (controlled). */
	selectedId?: string;
	/** Fired when a selectable node is clicked. */
	onSelect?: (id: string, node: NavTreeNode) => void;
	/** Show the search/filter box above the tree. Default: true. */
	searchable?: boolean;
	/** Placeholder for the search box (i18n key unless `skipTranslation`). */
	searchPlaceholder?: string;
	/** Ids of branches expanded on first render. */
	defaultExpandedIds?: string[];
	/** Also expand ancestors of the initially-selected node. Default: true. */
	expandParentsOfSelected?: boolean;
	/** Allow branch (parent) nodes to fire `onSelect` in addition to toggling. Default: false. */
	selectableBranches?: boolean;
	/** Message when a search yields no results (i18n key unless `skipTranslation`). */
	emptyLabel?: string;
	/** Treat `label`/`searchPlaceholder`/`emptyLabel` as literal strings, not i18n keys. */
	skipTranslation?: boolean;
	/** Outermost wrapper. */
	containerClassName?: string;
	/** The search box wrapper. */
	searchClassName?: string;
	/** The scrollable tree container. */
	treeClassName?: string;
	/** Each node row (merged onto every row). */
	itemClassName?: string;
}

/** Walk the tree and collect the ancestor ids leading to `targetId`. */
function findAncestorIds(
	nodes: NavTreeNode[],
	targetId: string,
	trail: string[] = []
): string[] | null {
	for (const node of nodes) {
		if (node.id === targetId) {
			return trail;
		}
		if (node.children?.length) {
			const found = findAncestorIds(node.children, targetId, [...trail, node.id]);
			if (found) {
				return found;
			}
		}
	}
	return null;
}

/** Return a pruned copy of the tree keeping only branches that match or contain a match. */
function filterTree(nodes: NavTreeNode[], query: string): NavTreeNode[] {
	return nodes.reduce<NavTreeNode[]>((acc, node) => {
		const selfMatch = node.label.toLowerCase().includes(query);
		const filteredChildren = node.children?.length ? filterTree(node.children, query) : [];

		if (selfMatch || filteredChildren.length) {
			acc.push({
				...node,
				// If the node itself matches, keep its full subtree so it can still be browsed;
				// otherwise only keep the matching descendants.
				children: node.children?.length
					? selfMatch
						? node.children
						: filteredChildren
					: undefined,
			});
		}
		return acc;
	}, []);
}

/** Split `text` so the matched portion can be wrapped for highlighting. */
function highlightMatch(text: string, query: string): React.ReactNode {
	if (!query) {
		return text;
	}
	const index = text.toLowerCase().indexOf(query);
	if (index === -1) {
		return text;
	}
	return (
		<>
			{text.slice(0, index)}
			<mark className="bg-yellow-200 text-inherit rounded-[2px]">
				{text.slice(index, index + query.length)}
			</mark>
			{text.slice(index + query.length)}
		</>
	);
}

interface TreeRowsProps {
	nodes: NavTreeNode[];
	depth: number;
	selectedId?: string;
	expandedIds: Set<string>;
	forceExpanded: boolean;
	query: string;
	selectableBranches: boolean;
	itemClassName?: string;
	onToggle: (id: string) => void;
	onSelect: (node: NavTreeNode) => void;
}

const TreeRows: React.FC<TreeRowsProps> = ({
	nodes,
	depth,
	selectedId,
	expandedIds,
	forceExpanded,
	query,
	selectableBranches,
	itemClassName,
	onToggle,
	onSelect,
}) => {
	return (
		<ul className="list-none m-0 p-0">
			{nodes.map((node) => {
				const isBranch = !!node.children?.length;
				const isExpanded = forceExpanded || expandedIds.has(node.id);
				const isSelected = selectedId === node.id;
				const isSelectable = !node.disabled && (!isBranch || selectableBranches);

				const handleRowClick = () => {
					if (node.disabled) {
						return;
					}
					if (isBranch) {
						onToggle(node.id);
						if (selectableBranches) {
							onSelect(node);
						}
					} else {
						onSelect(node);
					}
				};

				const Icon = node.icon;

				return (
					<li key={node.id}>
						<button
							type="button"
							disabled={node.disabled}
							aria-expanded={isBranch ? isExpanded : undefined}
							aria-current={isSelected ? "true" : undefined}
							onClick={handleRowClick}
							style={{ paddingLeft: depth * 16 + 8 }}
							className={cn(
								"group flex w-full items-center gap-1.5 py-1.5 pr-2 rounded-md text-left text-sm transition-colors",
								"text-gray-700 hover:bg-gray-100",
								isSelected && "bg-brand/10 text-brand font-medium hover:bg-brand/10",
								node.disabled && "opacity-50 cursor-not-allowed hover:bg-transparent",
								!isSelectable && !isBranch && "cursor-default",
								itemClassName
							)}
						>
							{/* Chevron slot — reserved even for leaves so labels align. */}
							<span className="flex h-4 w-4 shrink-0 items-center justify-center">
								{isBranch && (
									<ChevronRight
										className={cn(
											"h-4 w-4 text-gray-400 transition-transform",
											isExpanded && "rotate-90"
										)}
									/>
								)}
							</span>

							{Icon && <Icon className="h-4 w-4 shrink-0 text-gray-500" />}

							<span className="truncate">{highlightMatch(node.label, query)}</span>
						</button>

						{isBranch && isExpanded && (
							<TreeRows
								nodes={node.children!}
								depth={depth + 1}
								selectedId={selectedId}
								expandedIds={expandedIds}
								forceExpanded={forceExpanded}
								query={query}
								selectableBranches={selectableBranches}
								itemClassName={itemClassName}
								onToggle={onToggle}
								onSelect={onSelect}
							/>
						)}
					</li>
				);
			})}
		</ul>
	);
};

/**
 * AppNavTree — a reusable, searchable navigation tree (VS Code settings-style).
 *
 * Branch nodes expand/collapse; leaf nodes are selectable. The built-in search
 * box filters the tree, auto-expanding and highlighting matching branches.
 * Customise via the various `*ClassName` props rather than styling props.
 */
const AppNavTree: React.FC<AppNavTreeProps> = ({
	nodes,
	selectedId,
	onSelect,
	searchable = true,
	searchPlaceholder = "SEARCH",
	defaultExpandedIds,
	expandParentsOfSelected = true,
	selectableBranches = false,
	emptyLabel = "NO_RESULTS",
	skipTranslation = false,
	containerClassName,
	searchClassName,
	treeClassName,
	itemClassName,
}) => {
	const { t } = useTranslation();
	const tr = (key: string) => (skipTranslation ? key : (t(key as any) as string));

	const [query, setQuery] = useState("");
	const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
		const initial = new Set<string>(defaultExpandedIds ?? []);
		if (expandParentsOfSelected && selectedId) {
			const ancestors = findAncestorIds(nodes, selectedId);
			ancestors?.forEach((id) => initial.add(id));
		}
		return initial;
	});

	const normalizedQuery = query.trim().toLowerCase();
	const isSearching = searchable && normalizedQuery.length > 0;

	const displayNodes = useMemo(
		() => (isSearching ? filterTree(nodes, normalizedQuery) : nodes),
		[isSearching, nodes, normalizedQuery]
	);

	const handleToggle = (id: string) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const handleSelect = (node: NavTreeNode) => onSelect?.(node.id, node);

	return (
		<div className={cn("flex flex-col w-full", containerClassName)}>
			{searchable && (
				<div className={cn("relative mb-2", searchClassName)}>
					<Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
					<input
						type="text"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder={tr(searchPlaceholder)}
						className={cn(
							"flex h-9 w-full rounded-md border border-gray-300 bg-white pl-8 pr-3 py-1 text-sm text-gray-900",
							"placeholder:text-gray-400 transition-colors",
							"focus:border-blue-900 focus:outline-none focus:ring-0"
						)}
					/>
				</div>
			)}

			<div className={cn("overflow-auto", treeClassName)}>
				{displayNodes.length === 0 ? (
					<p className="px-2 py-3 text-sm text-gray-400">{tr(emptyLabel)}</p>
				) : (
					<TreeRows
						nodes={displayNodes}
						depth={0}
						selectedId={selectedId}
						expandedIds={expandedIds}
						forceExpanded={isSearching}
						query={normalizedQuery}
						selectableBranches={selectableBranches}
						itemClassName={itemClassName}
						onToggle={handleToggle}
						onSelect={handleSelect}
					/>
				)}
			</div>
		</div>
	);
};

AppNavTree.displayName = "AppNavTree";

export default AppNavTree;
